import React, { useState } from 'react';
import { NewBaseTable, StyledDragRefContainer } from 'components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical } from '@fortawesome/free-solid-svg-icons';
import theme from 'themes';
import { ColumnConfig, SortState } from 'components/NewBaseTable/NewBaseTable';

interface ProcessCardProps {
  defaultSortState: SortState;
  isEditing: boolean;
  processData?: Record<string, unknown>;
  columns: ColumnConfig[];
  isLoading?: boolean;
  processKey: string;
  onDrag: (draggedKey: string, hoverKey: string) => void;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>; // Add this
  machineId:string | undefined;
  timeZone?: string;
}

const processHeartlandTitleMapping: Record<string, string> = {
  'process0': 'HPV + AIR',
  'process1': 'HPV + AVD',
  'process2': 'Product',
  'process3': 'External Cleaning',
  'process4': 'Internal CIP',
  'process5': 'Aseptic Barrier',
  'process6': 'N2 Flush',
  'process8': 'Stirrer'
};

const processSteubenTitleMapping: Record<string, string> = {
  'process0': 'HPV + AIR',
  'process1': 'HPV + AVD',
  'process2': 'Product',
  'process3': 'External Cleaning',
  'process4': 'Internal CIP',
  'process5': 'NVD',
};

const ProcessCard: React.FC<ProcessCardProps> = ({
  columns,
  defaultSortState,
  processData,
  isEditing,
  onDrag,
  processKey,
  machineId,
  timeZone
}) => {

  const [dragging, setDragging] = useState(false);
  
  const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
    if (!isEditing) return;
    setDragging(true);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', processKey);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const draggedKey = event.dataTransfer.getData('text/plain');
    if (draggedKey && draggedKey !== processKey) {
      onDrag(draggedKey, processKey);
    }
  };

  // Transform processData to match TableColumnConfigs[] if necessary
  const tableData = Array.isArray(processData)
    ? processData
    : processData && typeof processData === 'object'
    ? Object.entries(processData).map(([key, value]) => ({
        key,
        ...(typeof value === 'object' && value !== null ? value : {}) // To make TS happy :)
      }))
    : [];

    const columnSizing = true;   //Allow sizing columns of the NewBaseTable from headers
    const textAlign = 'left';

     // Determine title based on machineId
  const processTitle = (() => {
    if (
      [
        'bbc03b5b-ea3f-49f9-a24b-b5ca3b023249',
        '28b2d3a9-2074-4c58-ac8a-a0f16575829b',
        '2a64b2ff-9894-4a1f-b8e6-cd4ae068c34b',
      ].includes(machineId || '')
    ) {
      return processHeartlandTitleMapping[processKey.toLowerCase()] || processKey.replace(/(\D+)(\d+)/, '$1 $2');
    }

    if (machineId === 'dce5e9f4-bf5c-4b85-b506-e6183f2cd225') {
      return processSteubenTitleMapping[processKey.toLowerCase()] || processKey.replace(/(\D+)(\d+)/, '$1 $2');
    }

    return processKey.replace(/(\D+)(\d+)/, '$1 $2');
  })();

  return (
    <StyledDragRefContainer
      draggable={isEditing}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      style={{
        opacity: dragging ? 0.9 : 1,
        cursor: isEditing ? 'grab' : 'default',
      }}
    >
      <div className="process-section">
      <div className="process-icon">
       <h4 className="title-text">{processTitle}</h4> 
        {isEditing && (
          <FontAwesomeIcon
            icon={faGripVertical}
            color={theme.colors.darkBlue}
            style={{ marginLeft: 'auto', marginTop: 'auto', marginBottom: 'auto' }}
            size="xs"
          />
        )}
      </div>
      <NewBaseTable
        newTableData={tableData} // Show each process's data in its own table
        columnConfigs={columns}
        sortState={defaultSortState}
        hideTotalFetchCount={true}
        customClassName="process-analysis"
        columnSizingHeader={columnSizing}
        textAlign={textAlign}     
        disableVirtualize={true}
         />
      </div>
    </StyledDragRefContainer>
  );
};

export default ProcessCard;
